/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import {
    Blocks,
    Braces,
    Bug,
    ChartColumn,
    ChevronDown,
    CircleOff,
    Eye,
    EyeOff,
    Globe,
    HardDrive,
    Moon,
    Settings2,
    Sun,
    Trash2,
    UserCheck,
    UserX,
    Wrench,
    type LucideIcon,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import { NavLink } from 'react-router';

/* Import context functions */
import { useI18n } from '@/context/I18nContext';

/* Import i18n functions */
import { LANGUAGE_LABELS, type AppLanguage } from '@/i18n/translations';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';

/* Import components */
import { AppBrandV2, AppLoginBrand } from '@/components/layout/AppBrand';
import LanguageFlag from '@/components/layout/LanguageFlag';
import DashboardBreadcrumb, { type DashboardBreadcrumbItem } from '@/components/layout/DashboardBreadcrumb';

/* Import libraries */
import { clearEeCheckApiCache, getEeCheckApiCacheStats, type EeCheckApiCacheStats } from '@/api/support/eeCheckApiCache';
import {
    APP_NAV_BUTTON_ACTIVE_CLASS,
    APP_NAV_BUTTON_BASE_CLASS,
    APP_NAV_BUTTON_INACTIVE_CLASS,
    APP_NAV_MENU_DISABLED_CLASS,
    APP_NAV_MENU_ITEM_CLASS,
    APP_NAV_SEGMENTED_BUTTON_ACTIVE_CLASS,
    APP_NAV_SEGMENTED_BUTTON_BASE_CLASS,
    APP_NAV_SEGMENTED_BUTTON_INACTIVE_CLASS,
} from '@/lib/presentation/navigationStyles';

export type NavbarMenuEntry =
    | {
        kind: 'caption';
        label: string;
    }
    | {
        enabled: boolean;
        icon: LucideIcon;
        kind: 'item';
        label: string;
        route: string;
    }
    | {
        kind: 'divider';
    };

interface AppNavbarProps {
    accountActive: boolean;
    currentPageBreadcrumbItems: DashboardBreadcrumbItem[];
    dashboardMenuItems: NavbarMenuEntry[];
    debugMenuItems: NavbarMenuEntry[];
    demoMenuItems: NavbarMenuEntry[];
    toolsMenuItems: NavbarMenuEntry[];
    errorLogCount: number;
    hasSession: boolean;
    isDebugSectionEnabled: boolean;
    isDashboardMenuActive: boolean;
    isDemoSectionEnabled: boolean;
    isTechnicalDetailsEnabled: boolean;
    isToolsSectionEnabled: boolean;
    isDebugMenuActive: boolean;
    isDemoMenuActive: boolean;
    isToolsMenuActive: boolean;
    isAdvancedViewEnabled: boolean;
    language: AppLanguage;
    loginStatusLabel: string;
    sessionUsername: string;
    showDeveloperOptions: boolean;
    onClearSession: () => void;
    onDebugSectionEnabledChange: (isEnabled: boolean) => void;
    onDemoSectionEnabledChange: (isEnabled: boolean) => void;
    onTechnicalDetailsEnabledChange: (isEnabled: boolean) => void;
    onToolsSectionEnabledChange: (isEnabled: boolean) => void;
    onAdvancedViewEnabledChange: (isEnabled: boolean) => void;
    onThemeChange: (theme: 'light' | 'dark') => void;
    setLanguage: (language: AppLanguage) => void;
    theme: 'light' | 'dark';
}

function formatStorageSize(bytes: number | null, locale: string): string {
    return new Intl.NumberFormat(locale, {
        maximumFractionDigits: 1,
    }).format((bytes ?? 0) / (1024 * 1024));
}

function AppNavbar({
    accountActive,
    currentPageBreadcrumbItems,
    dashboardMenuItems,
    debugMenuItems,
    demoMenuItems,
    toolsMenuItems,
    errorLogCount,
    hasSession,
    isDebugSectionEnabled,
    isDashboardMenuActive,
    isDemoSectionEnabled,
    isTechnicalDetailsEnabled,
    isToolsSectionEnabled,
    isDebugMenuActive,
    isDemoMenuActive,
    isToolsMenuActive,
    isAdvancedViewEnabled,
    language,
    loginStatusLabel,
    sessionUsername,
    showDeveloperOptions,
    onClearSession,
    onDebugSectionEnabledChange,
    onDemoSectionEnabledChange,
    onTechnicalDetailsEnabledChange,
    onToolsSectionEnabledChange,
    onAdvancedViewEnabledChange,
    onThemeChange,
    setLanguage,
    theme,
}: AppNavbarProps) {
    const { locale, t } = useI18n();
    const [cacheStats, setCacheStats] = useState<EeCheckApiCacheStats>({
        cacheEntryCount: 0,
        cacheSizeBytes: 0,
        storageQuotaBytes: null,
        storageUsageBytes: null,
    });

    async function loadCacheStats(): Promise<void> {
        setCacheStats(await getEeCheckApiCacheStats());
    }

    useEffect(() => {
        void loadCacheStats();
    }, []);

    const cacheQuotaPercent = cacheStats.storageQuotaBytes && cacheStats.storageUsageBytes !== null
        ? (cacheStats.storageUsageBytes / cacheStats.storageQuotaBytes) * 100
        : null;
    const cacheFreeBytes = cacheStats.storageQuotaBytes !== null && cacheStats.storageUsageBytes !== null
        ? Math.max(cacheStats.storageQuotaBytes - cacheStats.storageUsageBytes, 0)
        : null;
    const cacheFreePercent = cacheStats.storageQuotaBytes && cacheFreeBytes !== null
        ? (cacheFreeBytes / cacheStats.storageQuotaBytes) * 100
        : null;

    return (
        <nav className="app-navbar sticky top-0 z-40 border-b border-base-300">
            <div className="flex w-full flex-wrap items-center gap-3 px-4 py-3 lg:px-6">
                <NavLink
                    to={hasSession ? APP_ROUTES.home : APP_ROUTES.login}
                    className={`app-navbar__brand app-navbar__brand--v2 ${APP_NAV_BUTTON_BASE_CLASS} ${APP_NAV_BUTTON_INACTIVE_CLASS} text-lg font-semibold no-underline`}
                >
                    {hasSession
                        ? <AppLoginBrand className="app-login-brand--navbar" language={language} theme={theme} />
                        : <AppBrandV2 className="app-brand-v2--navbar" />}
                </NavLink>

                <div className="flex flex-wrap items-center gap-2">
                    <div className="dropdown">
                        <div
                            tabIndex={0}
                            role="button"
                            className={`${APP_NAV_BUTTON_BASE_CLASS} ${
                                isDashboardMenuActive
                                    ? APP_NAV_BUTTON_ACTIVE_CLASS
                                    : APP_NAV_BUTTON_INACTIVE_CLASS
                            }`}
                        >
                            <Blocks className="h-4 w-4" aria-hidden="true" />
                            {t('Dashboard')}
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                        </div>
                        <ul
                            tabIndex={0}
                            className="menu dropdown-content app-navbar__menu z-50 mt-3 w-56 rounded-box bg-base-100 p-2 shadow-xl"
                        >
                            {dashboardMenuItems.map((item, index) => {
                                if (item.kind === 'caption') {
                                    return (
                                        <li key={`dashboard-caption-${index}`} className="app-navbar__menu-caption-item">
                                            <span aria-disabled="true">{item.label}</span>
                                        </li>
                                    );
                                }

                                if (item.kind === 'divider') {
                                    return (
                                        <li
                                            key={`dashboard-divider-${index}`}
                                            className="app-navbar__menu-divider-item pointer-events-none leading-none select-none"
                                        >
                                            <hr className="app-navbar__menu-divider" />
                                        </li>
                                    );
                                }

                                return (
                                    <li
                                        key={item.route}
                                        className="my-1"
                                    >
                                        {item.enabled ? (
                                            <NavLink
                                                className={({ isActive }) =>
                                                    `${APP_NAV_MENU_ITEM_CLASS} ${isActive ? 'menu-active' : ''}`
                                                }
                                                to={item.route}
                                            >
                                                <item.icon className="h-4 w-4" aria-hidden="true" />
                                                {item.label}
                                            </NavLink>
                                        ) : (
                                            <span
                                                className={APP_NAV_MENU_DISABLED_CLASS}
                                                aria-disabled="true"
                                            >
                                                <item.icon className="h-4 w-4" aria-hidden="true" />
                                                {item.label}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>

                    {isDemoSectionEnabled ? (
                    <div className="dropdown">
                        <div
                            tabIndex={0}
                            role="button"
                            className={`${APP_NAV_BUTTON_BASE_CLASS} ${
                                isDemoMenuActive
                                    ? APP_NAV_BUTTON_ACTIVE_CLASS
                                    : APP_NAV_BUTTON_INACTIVE_CLASS
                            }`}
                        >
                            <ChartColumn className="h-4 w-4" aria-hidden="true" />
                            {t('Demo')}
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                        </div>
                        <ul
                            tabIndex={0}
                            className="menu dropdown-content app-navbar__menu z-50 mt-3 w-56 rounded-box bg-base-100 p-2 shadow-xl"
                        >
                            {demoMenuItems.map((item, index) => {
                                if (item.kind === 'caption') {
                                    return (
                                        <li key={`demo-caption-${index}`} className="app-navbar__menu-caption-item">
                                            <span aria-disabled="true">{item.label}</span>
                                        </li>
                                    );
                                }

                                if (item.kind === 'divider') {
                                    return (
                                        <li
                                            key={`demo-divider-${index}`}
                                            className="app-navbar__menu-divider-item pointer-events-none leading-none select-none"
                                        >
                                            <hr className="app-navbar__menu-divider" />
                                        </li>
                                    );
                                }

                                return (
                                    <li
                                        key={item.route}
                                        className="my-1"
                                    >
                                        {item.enabled ? (
                                            <NavLink
                                                className={({ isActive }) =>
                                                    `${APP_NAV_MENU_ITEM_CLASS} ${isActive ? 'menu-active' : ''}`
                                                }
                                                to={item.route}
                                            >
                                                <item.icon className="h-4 w-4" aria-hidden="true" />
                                                {item.label}
                                            </NavLink>
                                        ) : (
                                            <span
                                                className={APP_NAV_MENU_DISABLED_CLASS}
                                                aria-disabled="true"
                                            >
                                                <item.icon className="h-4 w-4" aria-hidden="true" />
                                                {item.label}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    ) : null}

                    {isToolsSectionEnabled ? (
                    <div className="dropdown">
                        <div
                            tabIndex={0}
                            role="button"
                            className={`${APP_NAV_BUTTON_BASE_CLASS} ${
                                isToolsMenuActive
                                    ? APP_NAV_BUTTON_ACTIVE_CLASS
                                    : APP_NAV_BUTTON_INACTIVE_CLASS
                            }`}
                        >
                            <Wrench className="h-4 w-4" aria-hidden="true" />
                            {t('Tools')}
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                        </div>
                        <ul
                            tabIndex={0}
                            className="menu dropdown-content app-navbar__menu z-50 mt-3 w-56 rounded-box bg-base-100 p-2 shadow-xl"
                        >
                            {toolsMenuItems.map((item, index) => {
                                if (item.kind === 'caption') {
                                    return (
                                        <li key={`tools-caption-${index}`} className="app-navbar__menu-caption-item">
                                            <span aria-disabled="true">{item.label}</span>
                                        </li>
                                    );
                                }

                                if (item.kind === 'divider') {
                                    return (
                                        <li
                                            key={`tools-divider-${index}`}
                                            className="app-navbar__menu-divider-item pointer-events-none leading-none select-none"
                                        >
                                            <hr className="app-navbar__menu-divider" />
                                        </li>
                                    );
                                }

                                return (
                                    <li
                                        key={item.route}
                                        className="my-1"
                                    >
                                        {item.enabled ? (
                                            <NavLink
                                                className={({ isActive }) =>
                                                    `${APP_NAV_MENU_ITEM_CLASS} ${isActive ? 'menu-active' : ''}`
                                                }
                                                to={item.route}
                                            >
                                                <item.icon className="h-4 w-4" aria-hidden="true" />
                                                {item.label}
                                            </NavLink>
                                        ) : (
                                            <span
                                                className={APP_NAV_MENU_DISABLED_CLASS}
                                                aria-disabled="true"
                                            >
                                                <item.icon className="h-4 w-4" aria-hidden="true" />
                                                {item.label}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    ) : null}

                    {isDebugSectionEnabled ? (
                    <div className="dropdown">
                        <div
                            tabIndex={0}
                            role="button"
                            className={`${APP_NAV_BUTTON_BASE_CLASS} ${
                                isDebugMenuActive
                                    ? APP_NAV_BUTTON_ACTIVE_CLASS
                                    : APP_NAV_BUTTON_INACTIVE_CLASS
                            }`}
                        >
                            <Bug className="h-4 w-4" aria-hidden="true" />
                            {t('Debug')}
                            {errorLogCount > 0 ? (
                                <span
                                    className="badge badge-error h-4 min-w-4 border-0 px-1 text-[10px] leading-none"
                                    title={`${errorLogCount} ${t('Error Log')}`}
                                >
                                    {errorLogCount}
                                </span>
                            ) : null}
                            <ChevronDown className="h-4 w-4" aria-hidden="true" />
                        </div>
                        <ul
                            tabIndex={0}
                            className="menu dropdown-content app-navbar__menu z-50 mt-3 w-56 rounded-box bg-base-100 p-2 shadow-xl"
                        >
                            {debugMenuItems.map((item, index) => {
                                if (item.kind === 'caption') {
                                    return (
                                        <li key={`debug-caption-${index}`} className="app-navbar__menu-caption-item">
                                            <span aria-disabled="true">{item.label}</span>
                                        </li>
                                    );
                                }

                                if (item.kind === 'divider') {
                                    return (
                                        <li
                                            key={`debug-divider-${index}`}
                                            className="app-navbar__menu-divider-item pointer-events-none leading-none select-none"
                                        >
                                            <hr className="app-navbar__menu-divider" />
                                        </li>
                                    );
                                }

                                return (
                                    <li
                                        key={item.route}
                                        className="my-1"
                                    >
                                        {item.enabled ? (
                                            <NavLink
                                                className={({ isActive }) =>
                                                    `${APP_NAV_MENU_ITEM_CLASS} ${isActive ? 'menu-active' : ''}`
                                                }
                                                to={item.route}
                                            >
                                                <item.icon className="h-4 w-4" aria-hidden="true" />
                                                {item.label}
                                            </NavLink>
                                        ) : (
                                            <span
                                                className={APP_NAV_MENU_DISABLED_CLASS}
                                                aria-disabled="true"
                                            >
                                                <item.icon className="h-4 w-4" aria-hidden="true" />
                                                {item.label}
                                            </span>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                    ) : null}
                </div>

                <div className="app-navbar__title hidden flex-1 lg:flex lg:translate-y-2" aria-live="polite">
                    <DashboardBreadcrumb
                        className="px-0"
                        items={currentPageBreadcrumbItems}
                    />
                </div>

                <div className="ml-auto flex items-center gap-2">
                    <div className="dropdown dropdown-end">
                        <div
                            tabIndex={0}
                            role="button"
                            className={`${APP_NAV_BUTTON_BASE_CLASS} ${
                                accountActive
                                    ? APP_NAV_BUTTON_ACTIVE_CLASS
                                    : APP_NAV_BUTTON_INACTIVE_CLASS
                            }`}
                            aria-label={`${t('Account')}, ${loginStatusLabel}${sessionUsername ? `: ${sessionUsername}` : ''}`}
                            title={sessionUsername || loginStatusLabel}
                            onClick={() => {
                                void loadCacheStats();
                            }}
                            onFocus={() => {
                                void loadCacheStats();
                            }}
                        >
                            {hasSession ? (
                                <UserCheck className="h-4 w-4 text-success" aria-hidden="true" />
                            ) : (
                                <UserX className="h-4 w-4 text-base-content/50" aria-hidden="true" />
                            )}
                            {hasSession && sessionUsername ? (
                                <span
                                    className="hidden max-w-40 truncate text-sm font-semibold text-base-content lg:inline"
                                    title={sessionUsername}
                                >
                                    {sessionUsername}
                                </span>
                            ) : (
                                <span className="hidden sm:inline">{loginStatusLabel}</span>
                            )}
                            <Settings2 className="h-4 w-4" aria-hidden="true" />
                        </div>

                        <div
                            tabIndex={0}
                            className="dropdown-content z-50 mt-3 w-100 rounded-box border border-base-300 bg-base-100 p-4 shadow-xl"
                        >
                            <div className="flex flex-col gap-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                            {t('Session')}
                                        </div>
                                        <div className="mt-1 flex items-center gap-2 text-sm font-medium">
                                            {hasSession ? (
                                                <UserCheck className="h-4 w-4 text-success" aria-hidden="true" />
                                            ) : (
                                                <UserX className="h-4 w-4 text-base-content/50" aria-hidden="true" />
                                            )}
                                            {hasSession && sessionUsername ? (
                                                <>
                                                    <span>{t('Logged in as')}</span>
                                                    <code className="rounded bg-base-200 px-1.5 py-0.5 text-xs font-medium">
                                                        {sessionUsername}
                                                    </code>
                                                </>
                                            ) : (
                                                <span>{loginStatusLabel}</span>
                                            )}
                                        </div>
                                    </div>

                                    {hasSession ? (
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                className="btn btn-outline btn-sm"
                                                onClick={onClearSession}
                                            >
                                                {t('Logout')}
                                            </button>
                                        </div>
                                    ) : (
                                        <NavLink
                                            className="btn btn-outline btn-sm"
                                            to={APP_ROUTES.login}
                                        >
                                            {t('Login')}
                                        </NavLink>
                                    )}
                                </div>

                                {showDeveloperOptions ? (
                                    <div className="border-t border-base-300 pt-4">
                                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                        <HardDrive className="h-4 w-4" aria-hidden="true" />
                                        <span>{t('Cache')}</span>
                                    </div>
                                    <div className="flex flex-col gap-2 rounded-box bg-base-200/60 p-3 text-sm">
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-base-content/70">{t('Entries')}</span>
                                            <span className="font-medium">{cacheStats.cacheEntryCount}</span>
                                        </div>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-base-content/70">{t('Used')}</span>
                                            <span className="font-medium">{formatStorageSize(cacheStats.cacheSizeBytes, locale)} MB</span>
                                        </div>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-base-content/70">{t('Storage usage')}</span>
                                            <span className="font-medium">
                                                {cacheStats.storageUsageBytes === null
                                                    ? t('Not available')
                                                    : `${formatStorageSize(cacheStats.storageUsageBytes, locale)} MB`}
                                                {cacheStats.storageQuotaBytes !== null ? ` ${t('of')} ${formatStorageSize(cacheStats.storageQuotaBytes, locale)} MB` : ''}
                                                {cacheQuotaPercent !== null ? ` (${cacheQuotaPercent.toFixed(1)}%)` : ''}
                                            </span>
                                        </div>
                                        <div className="flex items-center justify-between gap-3">
                                            <span className="text-base-content/70">{t('Available')}</span>
                                            <span className="font-medium">
                                                {cacheFreeBytes === null
                                                    ? t('Not available')
                                                    : `${formatStorageSize(cacheFreeBytes, locale)} MB`}
                                                {cacheStats.storageQuotaBytes !== null ? ` ${t('of')} ${formatStorageSize(cacheStats.storageQuotaBytes, locale)} MB` : ''}
                                                {cacheFreePercent !== null ? ` (${cacheFreePercent.toFixed(1)}%)` : ''}
                                            </span>
                                        </div>
                                    </div>
                                    <button
                                        type="button"
                                        className="btn btn-outline btn-sm mt-3 w-full"
                                        onClick={async () => {
                                            clearEeCheckApiCache();
                                            await loadCacheStats();
                                        }}
                                    >
                                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                                        {t('Clear cache')}
                                    </button>
                                    </div>
                                ) : null}

                                <div className="border-t border-base-300 pt-4">
                                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                        <Globe className="h-4 w-4" aria-hidden="true" />
                                        <span>{t('Language')}</span>
                                    </div>
                                    <div className="join w-full">
                                        {(['de', 'en', 'es'] as AppLanguage[]).map((currentLanguage) => (
                                            <button
                                                key={currentLanguage}
                                                type="button"
                                                className={`${APP_NAV_SEGMENTED_BUTTON_BASE_CLASS} ${
                                                    language === currentLanguage
                                                        ? APP_NAV_SEGMENTED_BUTTON_ACTIVE_CLASS
                                                        : APP_NAV_SEGMENTED_BUTTON_INACTIVE_CLASS
                                                }`}
                                                onClick={() => {
                                                    setLanguage(currentLanguage);
                                                }}
                                                aria-label={`${t('Language')}: ${LANGUAGE_LABELS[currentLanguage]}`}
                                                title={LANGUAGE_LABELS[currentLanguage]}
                                            >
                                                <LanguageFlag language={currentLanguage} />
                                            </button>
                                        ))}
                                    </div>
                                </div>

                                <div className="border-t border-base-300 pt-4">
                                    <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                        <Sun className="h-4 w-4" aria-hidden="true" />
                                        <span>{t('Appearance')}</span>
                                    </div>
                                    <div className="flex items-center justify-between gap-3 text-sm font-medium">
                                        <span>{t('Appearance')}</span>
                                        <label className="toggle border border-base-300 bg-base-200 text-base-content">
                                            <input
                                                type="checkbox"
                                                value="dark"
                                                className="theme-controller"
                                                checked={theme === 'dark'}
                                                onChange={(event) => {
                                                    onThemeChange(event.target.checked ? 'dark' : 'light');
                                                }}
                                                aria-label={t('Toggle light and dark theme')}
                                            />
                                            <Sun className="h-4 w-4" aria-hidden="true" />
                                            <Moon className="h-4 w-4" aria-hidden="true" />
                                        </label>
                                    </div>
                                </div>

                                {showDeveloperOptions ? (
                                    <div className="border-t border-base-300 pt-4">
                                        <div className="mb-2 flex items-center gap-2 text-xs font-semibold uppercase tracking-[0.08em] text-base-content/60">
                                            <Bug className="h-4 w-4" aria-hidden="true" />
                                        <span>{t('Debugging')}</span>
                                    </div>
                                    <div className="flex flex-col gap-3">
                                        <div className="flex items-center justify-between gap-3 text-sm font-medium">
                                            <span>{t('Demo section')}</span>
                                            <label className="toggle border border-base-300 bg-base-200 text-base-content">
                                                <input
                                                    type="checkbox"
                                                    checked={isDemoSectionEnabled}
                                                    onChange={(event) => {
                                                        onDemoSectionEnabledChange(event.target.checked);
                                                    }}
                                                    aria-label={t('Enable demo section')}
                                                />
                                                <CircleOff className="h-4 w-4" aria-hidden="true" />
                                                <ChartColumn className="h-4 w-4" aria-hidden="true" />
                                            </label>
                                        </div>
                                        <div className="flex items-center justify-between gap-3 text-sm font-medium">
                                            <span>{t('Tools section')}</span>
                                            <label className="toggle border border-base-300 bg-base-200 text-base-content">
                                                <input
                                                    type="checkbox"
                                                    checked={isToolsSectionEnabled}
                                                    onChange={(event) => {
                                                        onToolsSectionEnabledChange(event.target.checked);
                                                    }}
                                                    aria-label={t('Enable tools section')}
                                                />
                                                <CircleOff className="h-4 w-4" aria-hidden="true" />
                                                <Wrench className="h-4 w-4" aria-hidden="true" />
                                            </label>
                                        </div>
                                        <div className="flex items-center justify-between gap-3 text-sm font-medium">
                                            <span>{t('Debug section')}</span>
                                            <label className="toggle border border-base-300 bg-base-200 text-base-content">
                                                <input
                                                    type="checkbox"
                                                    checked={isDebugSectionEnabled}
                                                    onChange={(event) => {
                                                        onDebugSectionEnabledChange(event.target.checked);
                                                    }}
                                                    aria-label={t('Enable debug section')}
                                                />
                                                <CircleOff className="h-4 w-4" aria-hidden="true" />
                                                <Bug className="h-4 w-4" aria-hidden="true" />
                                            </label>
                                        </div>
                                        <div className="flex items-center justify-between gap-3 text-sm font-medium">
                                            <span>{t('Advanced view')}</span>
                                            <label className="toggle border border-base-300 bg-base-200 text-base-content">
                                                <input
                                                    type="checkbox"
                                                    checked={isAdvancedViewEnabled}
                                                    onChange={(event) => {
                                                        onAdvancedViewEnabledChange(event.target.checked);
                                                    }}
                                                    aria-label={t('Enable advanced view')}
                                                />
                                                <EyeOff className="h-4 w-4" aria-hidden="true" />
                                                <Eye className="h-4 w-4" aria-hidden="true" />
                                            </label>
                                        </div>
                                        <div className="flex items-center justify-between gap-3 text-sm font-medium">
                                            <span>{t('Show technical details')}</span>
                                            <label className="toggle border border-base-300 bg-base-200 text-base-content">
                                                <input
                                                    type="checkbox"
                                                    checked={isTechnicalDetailsEnabled}
                                                    onChange={(event) => {
                                                        onTechnicalDetailsEnabledChange(event.target.checked);
                                                    }}
                                                    aria-label={t('Show technical details')}
                                                />
                                                <EyeOff className="h-4 w-4" aria-hidden="true" />
                                                <Braces className="h-4 w-4" aria-hidden="true" />
                                            </label>
                                        </div>
                                    </div>
                                    </div>
                                ) : null}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="app-navbar__title-mobile flex w-full basis-full lg:hidden" aria-live="polite">
                    <DashboardBreadcrumb
                        className="px-0"
                        items={currentPageBreadcrumbItems}
                    />
                </div>
            </div>
        </nav>
    );
}

export default AppNavbar;
